import { useRef, useState } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { createComment } from "@/hooks/useCommentOperations";
import { plainTextToHTML } from "@/lib/legacyToHtml";
import { cn } from "@/lib/utils";

interface QuickCommentPopoverProps {
  backlogItemId: string;
  ticketNumber: number;
  className?: string;
}

/**
 * Say something about a ticket without leaving the list.
 *
 * Most comments on a backlog row are one line — "picked this up", "waiting on
 * legal" — and making that a page navigation is most of the reason they never
 * get written. The composer is deliberately plain text, but it writes through
 * `createComment`, the same path the ticket page's editor uses, and stores the
 * same paragraph HTML, so a comment written here is indistinguishable from one
 * written there (mentions and the realtime feed included). Anything that wants
 * formatting or an @-mention picker opens the ticket.
 */
const QuickCommentPopover = ({ backlogItemId, ticketNumber, className }: QuickCommentPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = async () => {
    const body = plainTextToHTML(text);
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      await createComment({ backlogItemId, commentText: body, queryClient });
      toast.success(`Comment added to #${ticketNumber}`);
      setText("");
      setOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Comment on #${ticketNumber}`}
              className={cn(
                "press-pop shrink-0 rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-primary data-[state=open]:text-primary dark:hover:text-[hsl(var(--ring))] dark:data-[state=open]:text-[hsl(var(--ring))]",
                className,
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Add a comment</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 border-border bg-card p-3"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
      >
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Comment on #{ticketNumber}
        </p>
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment…"
          rows={3}
          className="resize-none text-sm"
          onKeyDown={(e) => {
            // Enter alone stays a newline — this is a note, not a chat line.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground/70">⌘↵ to post</span>
          <Button size="sm" disabled={!text.trim() || submitting} onClick={() => void submit()} className="gap-1.5">
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Comment
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default QuickCommentPopover;
